import React, { useEffect, useState } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import SideDrawer from '../../../organisms/SideDrawer';
import BadgeStatus from '../../../atoms/BadgeStatus';
import StatusReasonModal from '../StatusReasonModal';
import GroupAboutTab from './GroupAboutTab';
import GroupMembersTab from './GroupMembersTab';
import GroupModelsTab from './GroupModelsTab';
import { useGroupDetail } from './useGroupDetail';

interface GroupDrawerProps {
    open: boolean;
    groupId: string | null;
    onClose: () => void;
    onChanged: () => void;
}

type GroupTab = 'members' | 'models' | 'about';

const GroupDrawer: React.FC<GroupDrawerProps> = ({ open, groupId, onClose, onChanged }) => {
    const detail = useGroupDetail(groupId, open, onChanged);
    const [tab, setTab] = useState<GroupTab>('members');
    const [toggling, setToggling] = useState(false);
    const { group, models } = detail;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isDefault, setIsDefault] = useState(false);

    useEffect(() => {
        if (open) setTab('members');
    }, [open, groupId]);

    useEffect(() => {
        if (group) {
            setName(group.name);
            setDescription(group.description);
            setIsDefault(group.defaultForNewContactPoints);
        }
    }, [group]);

    const active = group?.status === 'ACTIVE';
    const members = group?.users.filter((u) => u.active).length ?? 0;
    const blockDeactivate = active && models.length > 0;

    const aboutChanged =
        !!group &&
        (name.trim() !== group.name ||
            description !== group.description ||
            isDefault !== group.defaultForNewContactPoints);

    const resetAbout = () => {
        if (!group) return;
        setName(group.name);
        setDescription(group.description);
        setIsDefault(group.defaultForNewContactPoints);
    };

    const saveAbout = () => detail.saveInfo(name.trim(), description.trim(), isDefault);

    const tabs: { key: GroupTab; label: string; count?: number }[] = [
        { key: 'members', label: 'Membros', count: members },
        { key: 'models', label: 'Composições', count: models.length },
        { key: 'about', label: 'Sobre' },
    ];

    return (
        <>
            <SideDrawer
                open={open}
                title={group?.name ?? 'Grupo'}
                subtitle={group?.description || undefined}
                width="540px"
                onClose={onClose}
                titleAccessory={
                    group && (
                        <BadgeStatus color={active ? 'green' : 'red'} text={active ? 'Ativo' : 'Inativo'} />
                    )
                }
                headerActions={
                    group && (
                        <div className="flex items-center gap-2">
                            {tab === 'about' && (
                                <>
                                    <button
                                        type="button"
                                        title="Desfazer"
                                        disabled={!aboutChanged || detail.saving}
                                        onClick={resetAbout}
                                        className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <HiOutlineRefresh size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!aboutChanged || !name.trim() || detail.saving}
                                        onClick={saveAbout}
                                        className="px-4 py-2 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                                    >
                                        Salvar
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                disabled={blockDeactivate || detail.saving}
                                title={blockDeactivate ? 'Tire as composições do grupo antes de desativá-lo' : group.statusReason || ''}
                                onClick={() => setToggling(true)}
                                className={`px-4 py-2 rounded-md text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed ${
                                    active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-500 hover:bg-green-600'
                                }`}
                            >
                                {active ? 'Desativar' : 'Ativar'}
                            </button>
                        </div>
                    )
                }
            >
                {group && (
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700">
                            {tabs.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setTab(t.key)}
                                    className={`px-3 py-2 -mb-px border-b-2 text-sm ${
                                        tab === t.key
                                            ? 'border-teal-500 text-teal-800 dark:text-teal-200 font-medium'
                                            : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                                >
                                    {t.label}
                                    {t.count !== undefined && (
                                        <span className="ml-1.5 rounded-full bg-slate-100 dark:bg-slate-700 px-1.5 text-xs text-gray-500">{t.count}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        {tab === 'members' && (
                            <GroupMembersTab group={group} disabled={!active || detail.saving} onAdd={detail.addUser} onRemove={detail.removeUser} />
                        )}
                        {tab === 'models' && <GroupModelsTab models={models} />}
                        {tab === 'about' && (
                            <GroupAboutTab
                                group={group}
                                name={name}
                                description={description}
                                isDefault={isDefault}
                                onNameChange={setName}
                                onDescriptionChange={setDescription}
                                onDefaultChange={setIsDefault}
                            />
                        )}
                        {blockDeactivate && tab !== 'models' && (
                            <p className="text-[11px] text-gray-400">
                                Para desativar o grupo, mova ou retire os {models.length} modelo(s) vinculados.
                            </p>
                        )}
                    </div>
                )}
            </SideDrawer>
            <StatusReasonModal
                isOpen={toggling}
                title={active ? 'Desativar grupo' : 'Ativar grupo'}
                actionLabel={active ? 'Desativar' : 'Ativar'}
                onCancel={() => setToggling(false)}
                onConfirm={async (reason) => {
                    setToggling(false);
                    await detail.toggleStatus(reason);
                }}
            />
        </>
    );
};

export default GroupDrawer;
